import { type CreaAllegatoResponse } from '@framework/api-client/api/backoffice/schemas/creaAllegatoResponse.ts';
import { type ElencoFileDocumento } from '@framework/api-client/api/backoffice/schemas/elencoFileDocumento.ts';
import { type GetBackProcedimentiV2IdComunicazioniParams } from '@framework/api-client/api/backoffice/schemas/getBackProcedimentiV2IdComunicazioniParams.ts';
import { type ComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/comunicazioneDTO.ts';
import { type GetFrontRichiesteV2IdComunicazioniParams } from '@framework/api-client/api/inquiries-compile/schemas/getFrontRichiesteV2IdComunicazioniParams.ts';
import { type TipoComunicazioneDTO } from '@framework/api-client/api/inquiries-compile/schemas/tipoComunicazioneDTO.ts';
import React from 'react';
import { type CreateCommunicationRequest } from '../../../schema/communication/CreateCommunicationRequestSchema.ts';
import { type IGetCommunicationAttachmentQueryOptions } from '../form/attachments/useCommunicationAttachmentDownload.ts';
import { type IGetHydrateQueryOptions } from '../form/hydrate/useHydratedCommunicationContent.ts';

export interface ICommunicationsContext {
  /**
   * Valorizzato con l'id del procedimento selezionato, solamente
   * in caso di BackOffice
   */
  procedureId?: string;

  /**
   * Valorizzato con l'id dell'attività selezionata, solamente
   * in caso di BackOffice
   */
  taskId?: string;

  /**
   * Valorizzato con l'id della richiesta selezionata, solamente
   * in caso di FrontOffice
   */
  submissionId?: string | null;

  /**
   * Valorizzato con l'ID del procedimento padre (se siamo in un
   * endoprocedimento)
   */
  parentProcedureId: string | undefined | null;

  /**
   * Elenco dei tipi di comunicazioni ricercabili, inviabili,
   * caricabili manualmente e modificabili
   */
  searchableTypes: TipoComunicazioneDTO[];
  sendableTypes: TipoComunicazioneDTO[];
  loadableTypes: TipoComunicazioneDTO[];
  editableTypes: TipoComunicazioneDTO[];

  /**
   * Ricerca delle comunicazioni
   */
  query?:
    | GetFrontRichiesteV2IdComunicazioniParams
    | GetBackProcedimentiV2IdComunicazioniParams;

  /**
   * Imposta i parametri di ricerca
   */
  setQuery?: (
    params:
      | GetFrontRichiesteV2IdComunicazioniParams
      | GetBackProcedimentiV2IdComunicazioniParams,
  ) => void;

  /**
   * Indica se la procedura è aperta
   */
  isProcedureOpen?: boolean | null;

  /**
   * Azione intrapresa quando si seleziona una comunicazione da modificare
   */
  onEdit?: (communication: ComunicazioneDTO) => void;
  /**
   * Azione intrapresa quando si seleziona una comunicazione da eliminare
   */
  onDelete?: (id: string) => Promise<void>;

  /**
   * Azione intrapresa al salvataggio di una comunicazione
   */
  onSave?: (
    request: CreateCommunicationRequest,
    manual: boolean | null | undefined,
    options?: {
      /**
       * Se true, viene eseguita solo la chiamata di upsert. Non viene quindi eseguito il successivo onSave
       * per il refresh della lista né mostrati i messaggi di successo.
       * In questo modo è possibile usare la funzione anche solo come salvataggio per l'invio in 2 parti
       * (salvataggio nuova comunicazione -> invio).
       */
      saveOnly?: boolean | null;
    },
  ) => Promise<ComunicazioneDTO | undefined>;

  /**
   * Azione intrapresa all'invio di una comunicazione
   */
  onSend?: (communicationId: string) => Promise<ComunicazioneDTO | undefined>;

  /**
   * Segna una comunicazione ricevuta come letta. Se assente, il comando
   * di presa lettura non viene mostrato.
   */
  onMarkAsRead?: (communicationId: string) => Promise<void>;

  isEditable?: boolean;

  /**
   * Gestione della pre-compilazione delle comunicazioni (anteprima)
   * Usiamo la getOptions per poter fare la chiamata API corretta in base al tipo
   * di entità da cui inviamo la comunicazione (richiesta, procedimento, attività)
   * e forniamo il relativo ID
   */
  getHydrateCommunicationQueryOptions:
    | IGetHydrateQueryOptions
    | null
    | undefined;
  /**
   * Forniamo al context l'endpoint corretto per il recupero della lista degli allegati,
   * così da poter fare le chiamate API dall'intenro dei componenti comuni sia FO che BO.
   */
  getDownloadAttachmentQueryOptions: IGetCommunicationAttachmentQueryOptions;

  uploadAttachment?: (
    communicationTypeId: string,
    attachmentGroupId: string,
    file: Blob,
  ) => Promise<CreaAllegatoResponse | undefined>;
  attachExisting?: (
    communicationTypeId: string,
    attachmentGroupId: string,
    file: ElencoFileDocumento,
  ) => Promise<CreaAllegatoResponse | undefined>;

  /**
   * Indica se siamo in backoffice
   */
  isBackoffice: boolean;
}

export const CommunicationsContext =
  React.createContext<ICommunicationsContext>({
    parentProcedureId: undefined,
    getHydrateCommunicationQueryOptions: () => ({
      enabled: false,
      queryKey: [],
    }),
    getDownloadAttachmentQueryOptions: () => ({
      enabled: false,
      queryKey: [],
    }),
    searchableTypes: [],
    sendableTypes: [],
    loadableTypes: [],
    editableTypes: [],
    isBackoffice: false,
  });

/**
 * Context per gestire le informazioni comuni alle comunicazioni sia
 * sul FrontOffice che sul BackOffice.
 *
 * Al momento è utilizzato solamente per l'ufficio di comunicazione,
 * ma può essere esteso.
 */
export function CommunicationsContextProvider(
  props: React.PropsWithChildren<ICommunicationsContext>,
) {
  const {
    procedureId,
    taskId,
    submissionId,
    parentProcedureId,
    query,
    setQuery,
    searchableTypes,
    loadableTypes,
    sendableTypes,
    editableTypes,
    isProcedureOpen,
    onDelete,
    onEdit,
    isEditable,
    getHydrateCommunicationQueryOptions,
    getDownloadAttachmentQueryOptions,
    onSave,
    onSend,
    onMarkAsRead,
    uploadAttachment,
    attachExisting,
    isBackoffice,
  } = props;

  const contextValue = React.useMemo(
    () => ({
      procedureId,
      taskId,
      submissionId,
      parentProcedureId,
      query,
      setQuery,
      searchableTypes,
      loadableTypes,
      sendableTypes,
      editableTypes,
      isProcedureOpen,
      onDelete,
      onEdit,
      onSave,
      onSend,
      onMarkAsRead,
      isEditable,
      getHydrateCommunicationQueryOptions,
      getDownloadAttachmentQueryOptions,
      uploadAttachment,
      attachExisting,
      isBackoffice,
    }),
    [
      procedureId,
      taskId,
      submissionId,
      parentProcedureId,
      query,
      setQuery,
      searchableTypes,
      loadableTypes,
      sendableTypes,
      editableTypes,
      isProcedureOpen,
      onDelete,
      onEdit,
      isEditable,
      getHydrateCommunicationQueryOptions,
      getDownloadAttachmentQueryOptions,
      onSave,
      onSend,
      onMarkAsRead,
      uploadAttachment,
      attachExisting,
      isBackoffice,
    ],
  );

  return (
    <CommunicationsContext.Provider value={contextValue}>
      {props.children}
    </CommunicationsContext.Provider>
  );
}

/**
 * Hook per accedere al contesto delle comunicazioni
 */
export const useCommunicationsContext = () => {
  return React.useContext(CommunicationsContext);
};
